import { useId, useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'

import { useApiClient } from '@/api/ApiClientProvider'
import type { SortOrder } from '@/api/list'
import {
  ATTRIBUTION_MODELS,
  DEFAULT_ORDER_SORT,
  ORDER_CHANNELS,
  ORDER_CUSTOMER_TYPES,
  ORDER_SORTS,
  ORDERS_CSV_MAX_ROWS,
  ordersCsvFilename,
  type OrderAttributionModel,
  type OrderChannel,
  type OrderCustomerType,
  type OrderSort,
  type OrdersQuery,
} from '@/api/revenueLeakage'
import { Card, EmptyState, LoadingBlock, minorUnitExponent, type FormatOptions } from '@/design'
import { usePermissions } from '@/lib/access'
import type { GlobalFilters } from '@/lib/filters'
import { filtersKey } from '@/lib/useGlobalFilters'

import { CsvButton } from '../../list/CsvButton'
import { ListTable } from '../../list/ListTable'
import {
  CHANNEL_LABELS,
  COLUMN_CHOICE,
  CUSTOMER_TYPE_LABELS,
  describeReason,
  GROUP_LABELS,
  ORDER_COLUMNS,
  unavailableReason,
  type OrderCellContext,
} from './columns'
import { useRevenueOrders } from './useRevenueOrders'
import '../../list/list.css'
import './orders.css'

/** The page's slot props (`slots.tsx` `RevenueSlotProps`): the filters, the basis and how money reads. */
export interface OrdersTabProps {
  filters: GlobalFilters
  basis: 'gross' | 'net'
  money: FormatOptions
}

const MODEL_LABELS: Record<OrderAttributionModel, string> = {
  last_non_direct: 'Last non-direct',
  last_touch: 'Last touch',
  first_touch: 'First touch',
  paid_click: 'Paid click',
}

/** Payment providers the connector names most; any other is typed. `unknown` is none recorded. */
const KNOWN_PROVIDERS = ['cashflows', 'stripe', 'dna', 'unknown'] as const

/** The tab's own filters and sort: kept in the tab, never in the URL (the page owns `?tab=` and the basis). */
interface OrderFilterState {
  competitionId?: string
  channel?: OrderChannel
  customerType?: OrderCustomerType
  paymentProvider?: string
  /**
   * Gross at least this, in major units as typed. The API compares it with each order's
   * own recorded amount, so it is in the order's currency (the tenant's, for nearly all).
   */
  minGross?: number
  model: OrderAttributionModel
}

const NO_FILTERS: OrderFilterState = { model: 'last_non_direct' }

function isOrderSort(value: string): value is OrderSort {
  return (ORDER_SORTS as readonly string[]).includes(value)
}

/** The tab's filters as the route's query: min gross in the minor units of the tenant's currency. */
export function ordersQuery(
  filters: GlobalFilters,
  state: OrderFilterState,
  sort: { sort: OrderSort; order: SortOrder },
  currency: string,
): OrdersQuery {
  return {
    filters,
    sort: sort.sort,
    order: sort.order,
    model: state.model,
    ...(state.competitionId ? { competitionId: state.competitionId } : {}),
    ...(state.channel ? { channel: state.channel } : {}),
    ...(state.customerType ? { customerType: state.customerType } : {}),
    ...(state.paymentProvider ? { paymentProvider: state.paymentProvider } : {}),
    ...(state.minGross !== undefined && state.minGross > 0
      ? { minGrossMinor: Math.round(state.minGross * 10 ** minorUnitExponent(currency)) }
      : {}),
  }
}

function filtered(state: OrderFilterState): boolean {
  return (
    state.competitionId !== undefined ||
    state.channel !== undefined ||
    state.customerType !== undefined ||
    state.paymentProvider !== undefined ||
    state.minGross !== undefined
  )
}

/** A select of `options` with "Any" first; the empty choice is none. */
function Choice<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value: T | undefined
  options: readonly { value: T; label: string }[]
  onChange: (value: T | undefined) => void
}) {
  return (
    <label className="ci-field ci-field--inline list-filter">
      <span>{label}</span>
      <select value={value ?? ''} onChange={(event) => onChange(event.target.value === '' ? undefined : (event.target.value as T))}>
        <option value="">Any</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  )
}

/** A typed value, applied when the field is left or Enter is pressed rather than on every keystroke. */
function Typed({
  label,
  value,
  inputMode,
  list,
  onCommit,
  children,
}: {
  label: string
  value: string
  inputMode?: 'decimal'
  list?: string
  onCommit: (value: string) => void
  children?: ReactNode
}) {
  const [draft, setDraft] = useState(value)
  const [shown, setShown] = useState(value)
  // A reset from outside (Clear filters) replaces what was typed.
  if (shown !== value) {
    setShown(value)
    setDraft(value)
  }
  return (
    <label className="ci-field ci-field--inline list-filter">
      <span>{label}</span>
      <input
        type="text"
        inputMode={inputMode}
        list={list}
        value={draft}
        size={10}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={() => onCommit(draft.trim())}
        onKeyDown={(event) => {
          if (event.key === 'Enter') onCommit(draft.trim())
        }}
      />
      {children}
    </label>
  )
}

function OrderFilters({
  filters,
  state,
  onChange,
}: {
  filters: GlobalFilters
  state: OrderFilterState
  onChange: (next: OrderFilterState) => void
}) {
  const client = useApiClient()
  const competitions = useQuery({
    queryKey: ['revenue-stage-b', 'orders', 'competitions', client.kind, filtersKey(filters)],
    queryFn: () => client.competitions({ filters, sort: 'name', order: 'asc', limit: 200 }),
  })
  const set = (patch: Partial<OrderFilterState>) => onChange({ ...state, ...patch })
  const providerList = useId()
  return (
    <div className="rvo-filters" role="group" aria-label="Filter the orders">
      <Choice
        label="Competition"
        value={state.competitionId}
        options={(competitions.data?.data.rows ?? []).map((row) => ({ value: row.competitionId, label: row.name }))}
        onChange={(competitionId) => set({ competitionId })}
      />
      <Choice
        label="Channel"
        value={state.channel}
        options={ORDER_CHANNELS.map((channel) => ({ value: channel, label: CHANNEL_LABELS[channel] }))}
        onChange={(channel) => set({ channel })}
      />
      <Choice
        label="Credited by"
        value={state.model}
        options={ATTRIBUTION_MODELS.map((model) => ({ value: model, label: MODEL_LABELS[model] }))}
        onChange={(model) => set({ model: model ?? 'last_non_direct' })}
      />
      <Choice
        label="Customer"
        value={state.customerType}
        options={ORDER_CUSTOMER_TYPES.map((type) => ({ value: type, label: CUSTOMER_TYPE_LABELS[type] }))}
        onChange={(customerType) => set({ customerType })}
      />
      <Typed
        label="Payment provider"
        value={state.paymentProvider ?? ''}
        list={providerList}
        onCommit={(value) => set({ paymentProvider: value === '' ? undefined : value.toLowerCase() })}
      >
        <datalist id={providerList}>
          {KNOWN_PROVIDERS.map((provider) => (
            <option key={provider} value={provider} />
          ))}
        </datalist>
      </Typed>
      <Typed
        label="Gross at least, in the order’s currency"
        value={state.minGross === undefined ? '' : String(state.minGross)}
        inputMode="decimal"
        onCommit={(value) => {
          const amount = Number(value)
          set({ minGross: value === '' || !Number.isFinite(amount) || amount <= 0 ? undefined : amount })
        }}
      />
    </div>
  )
}

/**
 * The Revenue page's Orders tab: every completed order of the period, one row each, from
 * `GET /analytics/revenue/orders`. The API filters, sorts and pages by its keyset cursor,
 * so a header click asks it for a new order and "Show more" fetches the next page; the
 * CSV is the API's own (`format=csv`), never built here. Each row is in its own order's
 * currency; nothing names a person. A competition's name opens its page.
 */
export function OrdersTab({ filters, money }: OrdersTabProps) {
  const client = useApiClient()
  const { can } = usePermissions()
  const currency = money.currency ?? 'GBP'
  const [state, setState] = useState<OrderFilterState>(NO_FILTERS)
  const [sort, setSort] = useState<{ sort: OrderSort; order: SortOrder }>({ sort: DEFAULT_ORDER_SORT, order: 'desc' })
  const query = ordersQuery(filters, state, sort, currency)
  const list = useRevenueOrders(query)
  const { first, latest, rows } = list
  // While the previous list stands in for the next, its period is not the one a download would cover.
  const period = list.isPlaceholderData ? undefined : first?.meta.period

  const csv = can('exports.create') ? (
    <CsvButton
      key={JSON.stringify(query)}
      download={() => client.revenueOrdersCsv(query)}
      filters={filters}
      period={period}
      filename={ordersCsvFilename}
      noun="orders"
      maxRows={ORDERS_CSV_MAX_ROWS}
      hint="Order ids only: no names or emails"
    />
  ) : null

  const context: OrderCellContext | null = latest
    ? { notAvailable: {}, money: { ...money, currency: latest.data.currency }, timezone: latest.data.timezone }
    : null

  const empty = filtered(state) ? (
    <EmptyState
      variant="no-results"
      compact
      title="No orders match these filters"
      description="Try other filters, or clear them."
      action={
        <button type="button" className="ci-button" onClick={() => setState(NO_FILTERS)}>
          Clear filters
        </button>
      }
    />
  ) : (
    <EmptyState variant="no-results" compact title="No orders in this period" description="No order completed in the period and scope chosen in the filter bar." />
  )

  return (
    <Card
      title="Which orders made the period?"
      subtitle="Every completed order, with its competitions, payment, channel and refunds · each in its own currency"
      busy={list.isFetching && !list.isFetchingNextPage}
      actions={csv}
    >
      <div className="list-body">
        <OrderFilters filters={filters} state={state} onChange={setState} />
        {list.isPending ? (
          <LoadingBlock rows={6} label="Loading orders" />
        ) : !latest || !context ? (
          <EmptyState variant="outage" announce description="The orders could not be loaded just now. Try again in a moment." />
        ) : (
          <ListTable
            caption="Orders"
            columns={ORDER_COLUMNS}
            groupLabels={GROUP_LABELS}
            columnChoice={COLUMN_CHOICE}
            sort={sort}
            onSort={(next) =>
              setSort(next && isOrderSort(next.sort) ? { sort: next.sort, order: next.order } : { sort: DEFAULT_ORDER_SORT, order: 'desc' })
            }
            rows={rows}
            rowKey={(row) => row.orderId}
            context={context}
            rule={unavailableReason}
            describe={describeReason}
            empty={empty}
            footer={{ total: latest.data.total, noun: 'orders', paging: list.paging }}
          />
        )}
      </div>
    </Card>
  )
}
